// Read-only views for render / UI / input (IMPLEMENTATION §2.1 query.ts). They never mutate the state;
// render, ui, input and audio import this module (and commands/events/state types), never sim/rules or
// sim/systems.
import {
  cycleCost,
  isMachineType,
  isMatcher,
  isProcessorType,
  isSinkType,
  matcherDef,
  maxLevelOf,
  packerCap,
  packerDef,
  recipeById,
  simpleDef,
  slotFams,
  workPerTick,
} from './bdefs.ts';
import { undoBuildBlock } from './buildOps.ts';
import type { Dst, RejectReason, Src } from './commands.ts';
import { mulDivFloor } from './core/num.ts';
import type { Cell, Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import { isActive, type SimDefs } from './defs.ts';
import { famDef, findBuilding, findItem } from './entities.ts';
import { type ExpansionView, expansionView } from './expansions.ts';
import type { BType, EffectId, FamId, RecipeId, ShopId, TipId, UnlockId } from './gen/ids.ts';
import { ghostFill, ghostReqs, isGhostBuild } from './ghosts.ts';
import { autoPerSec, etaPerSec, hudAutoPerSec, incomeOver, perSec } from './income.ts';
import { buildOcc, isOpen, occAt, ROCK } from './occupancy.ts';
import { acceptReason } from './rules/accept.ts';
import {
  adviceInputsKey,
  bestUpgrade as bestUpgradeRule,
  type FactoryEstimate,
  factoryEstimate as factoryEstimateRule,
  pickBest as pickBestRule,
  type UpgradeOption,
  upgradeOptions as upgradeOptionsRule,
} from './rules/advice.ts';
import { type Bottleneck, bottleneckOf, busyPermille } from './rules/bottleneck.ts';
import { type DropOutcome, resolveDrop as resolveDropRule } from './rules/drop.ts';
import { type GalleryStatus, galleryStatus as galleryStatusRule } from './rules/galleryStatus.ts';
import {
  type EtaCase,
  type EtaFormat,
  etaCase,
  etaSeconds,
  type GoalView,
  goalView as goalViewRule,
} from './rules/goal.ts';
import { mergeAllBlock, quickMergeBlock } from './rules/merge.ts';
import { planPaint } from './rules/paint.ts';
import { priceOf as priceRule, shopBlock, shopVisible } from './rules/price.ts';
import { triggerHolds } from './rules/triggers.ts';
import { planUndoSell } from './rules/undo.ts';
import { crateValue } from './sales.ts';
import { graceOf } from './stall.ts';
import type { Building, FloorItem, ProcessorB, SimState } from './state.ts';
import { reservedAt } from './topology.ts';
import { activeTip } from './tutorial.ts';
import { hasEffect } from './unlocks.ts';

export type { AdviceReason, FactoryEstimate, UpgradeOption } from './rules/advice.ts';
export type { Bottleneck } from './rules/bottleneck.ts';
export type { DropOutcome } from './rules/drop.ts';
export type { Binding, GalleryStatus } from './rules/galleryStatus.ts';

/** A read-only context (fresh occupancy, lazily built topology) for the queries below. */
function readCtx(s: SimState, d: SimDefs): Ctx {
  return { s, d, ev: [], occ: buildOcc(s, d), topo: null };
}

/** Drag / tap-target preview: the exact outcome `drop` would have now (same function as the apply). */
export function resolveDrop(
  s: SimState,
  d: SimDefs,
  from: Src,
  to: Dst,
  build: boolean,
): DropOutcome {
  return resolveDropRule(s, d, buildOcc(s, d), from, to, build);
}

/**
 * Valid-target glows (M6, IMPLEMENTATION §2.7 / UX §3.9): the outcome `drop` would have on every open cell of
 * the current mask, in row-major order — the same resolver as the apply, on one shared occupancy index.
 */
export function dropTargets(
  s: SimState,
  d: SimDefs,
  from: Src,
  build: boolean,
): { x: Int; y: Int; out: DropOutcome }[] {
  const occ = buildOcc(s, d);
  const out: { x: Int; y: Int; out: DropOutcome }[] = [];
  const m = occ.mask;
  for (let y = m.y0; y <= m.y1; y++) {
    for (let x = m.x0; x <= m.x1; x++) {
      if (!isOpen(occ, x, y)) continue;
      out.push({ x, y, out: resolveDropRule(s, d, occ, from, { k: 'cell', x, y }, build) });
    }
  }
  return out;
}

/**
 * Chute-stroke preview (M6 PAINT): the cells `paintChutes(path)` would place or re-direct now
 * (the unaffordable tail and the source / sink buildings at the ends are not in `cells`), or why it would refuse.
 */
export function paintPreview(
  s: SimState,
  d: SimDefs,
  path: readonly Cell[],
): { ok: true; cells: Cell[]; cost: Int } | { ok: false; reason: RejectReason } {
  const plan = planPaint(s, d, buildOcc(s, d), path);
  if (!plan.ok) return plan;
  return { ok: true, cells: plan.steps.map((p) => ({ x: p.x, y: p.y })), cost: plan.cost };
}

/** An unlock effect is on (Build mode, the Chute tool, Quick Merge, the counter, …): input / UI gating. */
export function hasFeature(s: SimState, d: SimDefs, effect: EffectId): boolean {
  return hasEffect(s, d, effect);
}

export type CellView =
  | { k: 'rock' }
  | { k: 'empty' }
  | { k: 'item'; item: FloorItem }
  | { k: 'building'; b: Building };

export function cellView(s: SimState, d: SimDefs, x: Int, y: Int): CellView {
  const id = occAt(buildOcc(s, d), x, y);
  if (id === ROCK) return { k: 'rock' };
  if (id === 0) return { k: 'empty' };
  const it = findItem(s, id);
  if (it !== null) return { k: 'item', item: it };
  const b = findBuilding(s, id);
  return b !== null ? { k: 'building', b } : { k: 'empty' };
}

/** Loose items a drag of `id` would merge with (valid-target glow), ascending id. */
export function mergePartners(s: SimState, d: SimDefs, id: Int): Int[] {
  const it = findItem(s, id);
  if (it === null || it.tier >= famDef(d, it.fam).maxTier) return [];
  const out: Int[] = [];
  for (const o of s.floor)
    if (o.id !== id && o.fam === it.fam && o.tier === it.tier) out.push(o.id);
  return out;
}

/** Would building `bid` take one `fam` T`tier` by hand right now (valid-target glow)? null = yes. */
export function canAccept(
  s: SimState,
  d: SimDefs,
  bid: Int,
  fam: FamId,
  tier: Int,
): RejectReason | null {
  const b = findBuilding(s, bid);
  if (b === null) return 'stale';
  const c = readCtx(s, d);
  return acceptReason(c, b, fam, tier, null, reservedAt(c, b.x, b.y));
}

/**
 * The families building `bid` takes by hand right now (M6: the "{machine} takes {fam}" refusal text), by
 * the same rules as a hand drop — `acceptReason` (a full queue still counts as taking the family), a ghost
 * what it still needs (`ghostFill`) — in family-table order, active families only. `any` = it takes every
 * non-gun family (an empty Crate or Packer).
 */
export function handAccepts(s: SimState, d: SimDefs, bid: Int): { fams: FamId[]; any: boolean } {
  const b = findBuilding(s, bid);
  if (b === null) return { fams: [], any: false };
  const c = readCtx(s, d);
  const fams: FamId[] = [];
  let any = true;
  for (const f of d.families) {
    if (!isActive(d, f.version)) continue;
    let ok: boolean;
    if (b.ghost !== null) ok = ghostFill(d, b, f.id, f.maxTier) !== null;
    else {
      const why = acceptReason(c, b, f.id, 1, null, []);
      ok = why === null || why === 'full';
    }
    if (ok) fams.push(f.id);
    else if (f.kind !== 'gun') any = false;
  }
  return { fams, any };
}

export type UndoView = { ok: true; token: Int; value: Int } | { ok: false; reason: RejectReason };

/** Whether the undo toast's button works (R13: hidden with "Spent — can't undo" / "No room"). */
export function canUndoSell(s: SimState, d: SimDefs): UndoView {
  const sale = s.transient.lastSale;
  if (sale === null) return { ok: false, reason: 'stale' };
  const plan = planUndoSell(s, d, buildOcc(s, d), sale.token);
  return plan.ok ? { ok: true, token: sale.token, value: sale.value } : plan;
}

/** Build-mode Undo button (R13): entries left and whether the newest one can be undone now. */
export function canUndoBuild(s: SimState, d: SimDefs): { depth: Int; block: RejectReason | null } {
  return { depth: s.transient.undo.length, block: undoBuildBlock(s, readCtx(s, d)) };
}

/** Quick Merge on `id` would merge something (double-tap affordance). */
export function canQuickMerge(s: SimState, d: SimDefs, id: Int): RejectReason | null {
  return quickMergeBlock(s, d, id);
}

/** Merge All button state: null = ready and useful. */
export function mergeAllState(s: SimState, d: SimDefs): RejectReason | null {
  return mergeAllBlock(s, d);
}

/** Ticks until Merge All is off cooldown (0 = ready). */
export function mergeAllCooldown(s: SimState): Int {
  const left = s.progress.mergeAllReadyAt - s.tick;
  return left > 0 ? left : 0;
}

/** Shop card: price of the next copy (null = sold out) and why it can't be bought (null = it can). */
export function shopPrice(
  s: SimState,
  d: SimDefs,
  id: ShopId,
): { price: Int | null; block: RejectReason | null } {
  const block = shopBlock(s, d, id);
  if (block === 'stale') return { price: null, block };
  const price = priceRule(s, d, id);
  const why = block ?? (price === null ? 'max' : s.wallet.cash < price ? 'noCash' : null);
  return { price, block: why };
}

/** R9 status of Gallery `bid` (null when it isn't a Gallery). */
export function galleryStatus(s: SimState, d: SimDefs, bid: Int): GalleryStatus | null {
  const g = findBuilding(s, bid);
  return g !== null && g.type === 'gallery' ? galleryStatusRule(d, g) : null;
}

/** R17 bottleneck behind a STARVED Gallery `bid` (null when not starved / nothing to name). */
export function bottleneck(s: SimState, d: SimDefs, bid: Int): Bottleneck | null {
  const g = findBuilding(s, bid);
  return g !== null && g.type === 'gallery' ? bottleneckOf(readCtx(s, d), g) : null;
}

/** Busy share (permille) of a machine over the readout window (null for non-machines). */
export function machineBusy(s: SimState, d: SimDefs, bid: Int): Int | null {
  const b = findBuilding(s, bid);
  if (b === null || !('busy' in b)) return null;
  return busyPermille(readCtx(s, d), b);
}

/** A building's stall and whether it is shown yet (announced after graceTicks). */
export function stallOf(
  s: SimState,
  d: SimDefs,
  bid: Int,
): { stall: Building['stall']; shown: boolean } | null {
  const b = findBuilding(s, bid);
  if (b === null) return null;
  return { stall: b.stall, shown: b.stall !== null && b.stallT > graceOf(d, b.stall) };
}

/** Highest level a building type reaches by merging (render catalog / cards). */
export function maxLevel(d: SimDefs, type: BType): Int {
  return maxLevelOf(d, { type });
}

/**
 * Progress of a machine's running cycle (R2) or a Packer's merge action (R5), permille of its work cost;
 * null when nothing is in progress (no progress bar). Render reads it every frame: O(1), no allocation.
 */
export function progressPermille(d: SimDefs, b: Building): Int | null {
  if (b.type === 'packer') {
    if (b.prog <= 0) return null;
    const cost = packerDef(d).mergeCost;
    return b.prog >= cost ? 1000 : mulDivFloor(b.prog, 1000, cost);
  }
  if (!isProcessorType(b.type) || !('proc' in b) || b.proc === null) return null;
  const cost = cycleCost(d, b.type);
  return b.prog >= cost ? 1000 : mulDivFloor(b.prog, 1000, cost);
}

/** A building's stall id when it is shown (announced after graceTicks), else null. */
export function shownStall(d: SimDefs, b: Building): Building['stall'] {
  return b.stall !== null && b.stallT > graceOf(d, b.stall) ? b.stall : null;
}

/** Ghost card: requirement rows with what is filled so far. */
export function ghostView(
  s: SimState,
  d: SimDefs,
  bid: Int,
): { fam: FamId; tier: Int; n: Int; filled: Int }[] | null {
  const b = findBuilding(s, bid);
  if (b === null || b.ghost === null) return null;
  const g = b.ghost;
  return ghostReqs(d, b.type).map((r, i) => ({ ...r, filled: g.filled[i] ?? 0 }));
}

// ---- cards (M7) ---------------------------------------------------------------------------------------
// The UI's cards read these instead of re-deriving a rule (porting contract: ui stays thin).

/** Crate "Sell all" (R7): what the sale would pay now (the crateSellAll rate); null when `bid` is no Crate. */
export function crateSellValue(s: SimState, d: SimDefs, bid: Int): Int | null {
  const b = findBuilding(s, bid);
  if (b === null || b.type !== 'crate') return null;
  return crateValue(readCtx(s, d), b);
}

export interface StallDetail {
  /** The families the machine works from, slot order (a Smelter adds its fuel): the NO_INPUT words. */
  inputs: FamId[];
  /**
   * R4 laggard for a Loader / Gunsmith (the MISMATCH words): K = the highest tier across its slots and the
   * first slot family holding no T`k`; null for other buildings.
   */
  mismatch: { k: Int; lacking: FamId | null } | null;
}

/** What a machine card says about a stall (null for non-processors). */
export function stallDetail(s: SimState, d: SimDefs, bid: Int): StallDetail | null {
  const b = findBuilding(s, bid);
  if (b === null || !isProcessorType(b.type) || b.ghost !== null) return null;
  const p = b as ProcessorB;
  const inputs = slotFams(d, p).slice();
  if (p.type === 'smelter') inputs.push(d.buildings.smelter.fuel);
  if (!isMatcher(p.type)) return { inputs, mismatch: null };
  let k = 0;
  for (const slot of p.slots) for (const r of slot) if (r.tier > k) k = r.tier;
  let lacking: FamId | null = null;
  const fams = matcherDef(d, p.type).slots;
  for (let i = 0; i < fams.length; i++) {
    const slot = p.slots[i] ?? [];
    let has = false;
    for (const r of slot) if (r.tier === k) has = true;
    if (!has) {
      lacking = fams[i] ?? null;
      break;
    }
  }
  return { inputs, mismatch: { k, lacking } };
}

/**
 * Mean milliseconds per cycle of a processor at `level` (R2: cost ÷ work per tick; the work carries over, so
 * this is the steady rate), null for other types or a level it cannot reach.
 */
export function cycleMs(d: SimDefs, type: BType, level: Int): Int | null {
  if (!isProcessorType(type)) return null;
  const w = d.machineRules.workPerTick[level - 1];
  if (w === undefined || w <= 0) return null;
  return mulDivFloor(cycleCost(d, type), 1000, workPerTick(d, level) * d.tickHz);
}

/**
 * Building types that use family `fam` (the Info card's "Used by"), building-table order: active processors
 * whose active recipes take it, the Smelter's fuel, and for ammo the Packer (R5 packs ammo) and the Gallery.
 */
export function famConsumers(d: SimDefs, fam: FamId): BType[] {
  const out: BType[] = [];
  const add = (t: BType): void => {
    if (!out.includes(t)) out.push(t);
  };
  const takes = (rid: RecipeId): boolean => {
    const r = recipeById(d, rid);
    return isActive(d, r.version) && r.inputs.includes(fam);
  };
  const procs: ProcessorB['type'][] = [
    'caster',
    'grinder',
    'smelter',
    'press',
    'loader',
    'gunsmith',
  ];
  for (const t of procs) {
    if (isMatcher(t)) {
      const m = matcherDef(d, t);
      if (isActive(d, m.version) && takes(m.recipe)) add(t);
      continue;
    }
    const def = simpleDef(d, t);
    if (!isActive(d, def.version)) continue;
    for (const mode of def.modes) {
      if (!isActive(d, mode.version)) continue;
      for (const rid of mode.recipes) if (takes(rid)) add(t);
    }
    if (t === 'smelter' && d.buildings.smelter.fuel === fam) add(t);
  }
  if (fam === 'ammo') {
    add('packer');
    add('gallery');
  }
  return out;
}

/** The card's Pause button: the `pause` command takes this building (not a deposit, a chute or a ghost). */
export function canPause(s: SimState, _d: SimDefs, bid: Int): boolean {
  const b = findBuilding(s, bid);
  return b !== null && b.type !== 'deposit' && b.type !== 'chute' && b.ghost === null;
}

/**
 * Turning it changes what it does (the card's Rotate, the ⟳ handle): not a deposit (never turns) and not a
 * sink (Crate, Gallery, Dock accept on all four sides, GDD §4.4).
 */
export function canRotate(s: SimState, _d: SimDefs, bid: Int): boolean {
  const b = findBuilding(s, bid);
  return b !== null && b.type !== 'deposit' && !isSinkType(b.type);
}

/** A machine in the R2 sense (processors and the Packer): the first one built shows the rule banner. */
export function isMachine(type: BType): boolean {
  return isMachineType(type);
}

/** Packer target picker (R5): the current target (clamped to the cap) and the cap; null for non-Packers. */
export function packerTarget(s: SimState, d: SimDefs, bid: Int): { target: Int; cap: Int } | null {
  const b = findBuilding(s, bid);
  if (b === null || b.type !== 'packer') return null;
  const cap = packerCap(d, b.level, b.lockFam);
  return { target: b.target < cap ? b.target : cap, cap };
}

// ---- progression (M4a) -------------------------------------------------------------------------------

export type { ExpansionState, ExpansionView } from './expansions.ts';
export type { EtaCase, EtaFormat, GoalAdvice, GoalGhost, GoalView } from './rules/goal.ts';

export interface ShopCardView {
  id: ShopId;
  kind: 'deposit' | 'building';
  /** OreId for deposits, BType for buildings. */
  ref: string;
  /** Its unlock effects are on (u04 `shop` + `shop.deposit.<ore>` / `build.<type>`). */
  unlocked: boolean;
  /** Shown in the shop: unlocked and revealed (cash reached shopVisiblePermille of the price once). */
  visible: boolean;
  /** Price of the next copy; null = sold out. */
  price: Int | null;
  /** Why a drag would be refused regardless of the target cell (null = buyable now). */
  block: RejectReason | null;
  /** The next copy is a first-build ghost (materials by hand). */
  ghost: boolean;
  /** Chutes are painted with the Build Chute tool, not dragged from a card. */
  wired: boolean;
  /** Seconds until affordable at the current total income (0 = now, null = no income / no price). */
  etaSec: Int | null;
}

/**
 * The ETA case of `sec` seconds until affordable (the goal chip's rule, M4a #5): the UI formats the `etaSec`
 * of shop cards and the Expansion card with it and never recomputes an ETA (M4a #14).
 */
export function etaOf(sec: Int | null, fmt: EtaFormat): EtaCase {
  return etaCase(sec, fmt);
}

/** Every version-active shop item in shop order (the Shop sheet reads `visible`). */
export function shopView(s: SimState, d: SimDefs): ShopCardView[] {
  const perSec = etaPerSec(s, d);
  const out: ShopCardView[] = [];
  for (const item of d.shop) {
    if (!isActive(d, item.version)) continue;
    const { price, block } = shopPrice(s, d, item.id);
    const unlocked = shopBlock(s, d, item.id) === null;
    const type: BType = item.kind === 'deposit' ? 'deposit' : (item.ref as BType);
    out.push({
      id: item.id,
      kind: item.kind,
      ref: item.ref,
      unlocked,
      visible: shopVisible(s, d, item.id),
      price,
      block,
      ghost: isGhostBuild(s, d, type),
      wired: type === 'chute',
      etaSec: price === null ? null : etaSeconds(price, s.wallet.cash, perSec),
    });
  }
  return out;
}

/**
 * The goal chip: the current goal, its price and ETA (`fmt` = ui.json goalChip thresholds), or the best next
 * step it shows instead (R17 v2, `advice`). `best` = this state's `bestUpgrade`, when the caller already has it.
 */
export function goalView(
  s: SimState,
  d: SimDefs,
  fmt: EtaFormat,
  best?: UpgradeOption | null,
): GoalView | null {
  return goalViewRule(readCtx(s, d), fmt, best);
}

/**
 * R17 v2 best next step (lead decision L10): every priced, unlocked purchase that raises the factory model's
 * value (level-ups of on-line deposits, drills, machines and the Packer; a new Drill beside an on-line
 * deposit), best gain per $ first. Empty before a Loader or Packer feeds a Gallery. The ranking ignores cash.
 */
export function upgradeOptions(s: SimState, d: SimDefs): UpgradeOption[] {
  return upgradeOptionsRule(readCtx(s, d));
}

/** Changes whenever `upgradeOptions` may change (everything it reads besides cash): a cache key for the UI. */
export function adviceKey(s: SimState, d: SimDefs): string {
  return adviceInputsKey(readCtx(s, d));
}

/**
 * The goal chip's suggestion ("the affordable-or-next purchase"): the best option of the top rank tier that
 * the cash affords now, else the first of `upgradeOptions` (the next one to save for); null when none.
 */
export function bestUpgrade(s: SimState, d: SimDefs): UpgradeOption | null {
  return bestUpgradeRule(readCtx(s, d));
}

/** `bestUpgrade` from options ranked earlier (`upgradeOptions` ignores cash, so they can be reused). */
export function pickBest(opts: readonly UpgradeOption[], cash: Int): UpgradeOption | null {
  return pickBestRule(opts, cash);
}

/** The factory model's steady-state automated income of the current board (and its advice value). */
export function factoryEstimate(s: SimState, d: SimDefs): FactoryEstimate {
  return factoryEstimateRule(readCtx(s, d));
}

/** Board masks with their state and why the next one can't be bought (Expansion card). */
export function expansions(s: SimState, d: SimDefs): ExpansionView[] {
  return expansionView(s, d);
}

export interface IncomeView {
  /**
   * The HUD headline (R16, lead decision L12): total $/s over the ETA window (60 s; a young game averages
   * only the closed seconds). Shown at all times, before and after u18. Also the goal / shop ETA rate.
   */
  totalPerSec: Int;
  /**
   * Automated $/s over the whole ring as a FIXED 120 s window (not-yet-closed slots read $0): the u20 goal
   * progress, the u21 / u30 triggers and `completion` (M4a #6). Never the HUD's display value.
   */
  autoPerSec: Int;
  /**
   * The source of the HUD's secondary "auto $X/s" line (L12): automated $/s averaged over the buckets closed
   * since the first automated payout, up to 120 s (young window, L11). The UI smooths it for display
   * (`ui/hudRate.ts`, never in sim state). 0 until that payout's bucket has closed.
   */
  autoYoungPerSec: Int;
  /** The HUD shows the secondary auto line: from u18 (`unlocks.json` effect `hud.autoIncome`). */
  showAuto: boolean;
  manualPerSec: Int;
  /** Share of automated income over the ring, permille (M4b: ≥ 600‰ at completion). */
  autoSharePermille: Int;
}

export function incomeView(s: SimState, d: SimDefs): IncomeView {
  const ring = d.economy.income.ringBuckets;
  const all = incomeOver(s, d, 'total', ring).sum;
  const autoSum = incomeOver(s, d, 'auto', ring).sum;
  return {
    totalPerSec: etaPerSec(s, d),
    autoPerSec: autoPerSec(s, d),
    autoYoungPerSec: hudAutoPerSec(s, d) ?? 0,
    showAuto: hasEffect(s, d, 'hud.autoIncome'),
    manualPerSec: perSec(s, d, 'manual', ring),
    autoSharePermille: all > 0 ? mulDivFloor(autoSum, 1000, all) : 0,
  };
}

/** The active tutorial tip (copy key for the hint), or null; `index` = tips done or skipped. */
export function tutorialView(
  s: SimState,
  d: SimDefs,
): { index: Int; tip: { id: TipId; copy: string } | null } {
  const t = activeTip(s, d);
  return { index: s.progress.tutorial, tip: t === null ? null : { id: t.id, copy: t.copy } };
}

/** v0.1 completion (u21, §0.3): automated income ≥ $1,000/s AND an Ammo Box (T6) fired. */
export function completion(
  s: SimState,
  d: SimDefs,
): { complete: boolean; autoPerSec: Int; firedBest: Int } {
  return {
    complete: hasEffect(s, d, 'v01Complete'),
    autoPerSec: autoPerSec(s, d),
    firedBest: s.progress.firedBest,
  };
}

/** Would unlock `id`'s trigger hold now (debug panel / tests; ignores prerequisites). */
export function triggerHoldsNow(s: SimState, d: SimDefs, id: UnlockId): boolean {
  const u = d.unlocks.find((x) => x.id === id);
  return u !== undefined && triggerHolds(s, d, u.trigger);
}
